import React, { useEffect, useState } from 'react';
import { X, Loader2, Sparkles, AlertTriangle } from 'lucide-react';
import { createEvent, buildGeoInfoWithApprover, EVENT_TYPES, APPROVAL_STATUS } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './Toast';
import { FEEDBACK } from '../services/feedbackMessages';
import { DEFAULT_TIME_ZONE, timeZoneForWorker, todayInZone, zonedDateTimeToInstant } from '../lib/workerTime';
import { COMPENSATION_FULL_DAY_START_HM, COMPENSATION_FULL_DAY_END_HM } from '../lib/hourBalance';

// "Hoy" en la zona del trabajador que recibe la compensación.
const todayStr = (tz = DEFAULT_TIME_ZONE) => todayInZone(tz);

// Fecha + hora → instante en la zona del TRABAJADOR (no la del admin ni la
// del navegador), igual que el resto de modales de creación.
const buildIso = (date, hm, tz = DEFAULT_TIME_ZONE) => {
  if (!date || !hm) return null;
  const d = zonedDateTimeToInstant(date, hm, tz);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const AssignCompensationModal = ({ open, worker, onClose, onSubmitted }) => {
  const { user: authUser } = useAuth();
  const toast = useToast();
  const tz = timeZoneForWorker(worker);
  const [fecha, setFecha] = useState(() => todayStr(tz));
  // Una compensación es un DÍA LIBRE que el admin regala al trabajador como
  // contraprestación por horas extras acumuladas — no es jornada trabajada.
  // El par se crea al FINAL del día (23:58 → 23:59) y no a las 00:00: el
  // backend rechaza con 400 cualquier evento anterior a la hora de entrada
  // del trabajador si tiene enforceStartHour, y solo exime a los tipos 2 y 4
  // (el 8 no). Ambos extremos caen en el MISMO día de la zona del trabajador,
  // así que el día marcado es exactamente el mismo que antes. Ver la regla
  // completa en src/lib/hourBalance.js (isFullDayCompensationRange).
  const HORA_INICIO = COMPENSATION_FULL_DAY_START_HM;
  const HORA_FIN = COMPENSATION_FULL_DAY_END_HM;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setFecha(todayStr(tz));
      setError(null);
    }
  }, [open, tz]);

  if (!open || !worker?.id) return null;

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setError(null);
    const startIso = buildIso(fecha, HORA_INICIO, tz);
    const endIso = buildIso(fecha, HORA_FIN, tz);
    if (!startIso || !endIso) {
      setError('Fecha no válida.');
      return;
    }

    setSubmitting(true);
    try {
      // Creamos el par 8 (inicio compensación) + 9 (fin compensación) ya
      // aprobados — la asignación manual del admin no pasa por aprobación.
      // El admin que asigna queda registrado como aprobador implícito
      // dentro de geoInfo (audit trail estilo ;apprby=email:fecha).
      // Pasamos el objeto user entero: el helper usa el nombre completo y
      // muestra "TemControl" si quien lo aprueba es Mechagodzilla (type 5).
      const apprGeoInfo = buildGeoInfoWithApprover(null, authUser);
      await createEvent({
        workerId: worker.id,
        eventType: EVENT_TYPES.COMPENSATION_START,
        timestamp: startIso,
        approvalStatus: APPROVAL_STATUS.APPROVED,
        geoInfo: apprGeoInfo,
      });
      await createEvent({
        workerId: worker.id,
        eventType: EVENT_TYPES.COMPENSATION_END,
        timestamp: endIso,
        approvalStatus: APPROVAL_STATUS.APPROVED,
        geoInfo: apprGeoInfo,
      });
      toast.success(FEEDBACK.periodAssigned({ kind: 'compensacion', start: fecha, end: fecha, workerName: worker.name }));
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'No se pudo asignar la compensación.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold">Compensación</div>
            <h2 className="font-display text-lg font-bold text-ink">Asignar día de compensación</h2>
            <div className="text-xs text-ink-muted mt-0.5 truncate">A {worker.name}</div>
          </div>
          <button
            onClick={onClose}
            className="text-ink-muted hover:text-ink p-1 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div className="flex items-start gap-2.5 bg-violet-50 border border-violet-200 rounded-xl p-3 text-xs text-ink leading-relaxed">
            <Sparkles size={14} className="text-violet-600 mt-0.5 shrink-0" />
            <div>
              Le regalas a <b>{worker.name}</b> un <b>día libre</b> en la fecha indicada como contraprestación
              por horas extras acumuladas. Ese día le aparecerá en el calendario marcado como compensación
              y no tendrá que fichar.
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Fecha del día libre</label>
            <input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm"
              required
            />
          </div>

          {error && (
            <div className="text-xs text-danger bg-danger/5 border border-danger/20 rounded-lg px-3 py-2 flex items-start gap-2">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <div>{error}</div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-ink-muted hover:bg-slate-100 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary-dark transition-colors disabled:opacity-60"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              Asignar compensación
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignCompensationModal;
